import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';

interface LoginRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
}

export const LoginRequiredModal: React.FC<LoginRequiredModalProps> = ({
  isOpen,
  onClose,
  featureName = 'esta funcionalidade'
}) => {
  const navigate = useNavigate();

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl p-6 sm:p-8 text-center z-10 overflow-hidden"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Fechar modal"
            >
              <i className="fas fa-xmark text-sm"></i>
            </button>

            {/* Lock Icon Badge */}
            <div className="mx-auto w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 text-2xl shadow-lg shadow-black/40 mb-5 animate-in zoom-in-75 duration-300">
              <i className="fas fa-lock"></i>
            </div>

            {/* Badge pill */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-[10px] font-black uppercase tracking-wider mb-3">
              <i className="fas fa-shield-halved text-[9px]"></i>
              <span>Acesso Restrito</span>
            </div>

            {/* Title */}
            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-2">
              Login Necessário
            </h3>

            {/* Message */}
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-6">
              Você precisa fazer login para ter acesso a <span className="font-bold text-white bg-slate-800/80 px-2 py-0.5 rounded-md border border-white/5">{featureName}</span> e aproveitar todos os recursos do Cardumy.
            </p>

            {/* Perks list */}
            <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-3.5 mb-6 text-left space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                Ao criar sua conta grátis você pode:
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <i className="fas fa-check text-emerald-400 text-[10px] shrink-0"></i>
                <span>Gerenciar suas pastas e precificar coleções</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <i className="fas fa-check text-emerald-400 text-[10px] shrink-0"></i>
                <span>Montar, salvar e exportar decks no Deckbuilder</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <i className="fas fa-check text-emerald-400 text-[10px] shrink-0"></i>
                <span>Participar de torneios, trocas e da comunidade</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <i className="fas fa-check text-emerald-400 text-[10px] shrink-0"></i>
                <span>Comprar em lojas com segurança e carrinho</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/login');
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-3.5 px-6 rounded-2xl transition-all shadow-lg shadow-purple-600/25 active:scale-[0.98] text-sm flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="fas fa-arrow-right-to-bracket text-xs"></i>
                <span>Fazer Login ou Criar Conta</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full bg-slate-800/60 hover:bg-slate-800 text-slate-400 hover:text-white font-semibold py-2.5 px-4 rounded-xl transition-all text-xs border border-white/5 cursor-pointer"
              >
                Continuar Navegando
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
